import { useCallback, useState } from "react";
import { useAtom } from "jotai";
import { useSnackbar } from "notistack";
import { WarningDialog } from "../../../components/dialogs/WarningDialog";
import { ProductDeleteOptions } from "../../../features/products/product-modal-state";
import { productDbAtom } from "../../../features/products/product.state";
import { deleteDbProducts } from "../../../features/products/product.service";
import { removeCollectionProducts } from "../../../features/products/product";
import { useProductOps } from "../product-ops";

interface ProductDeleteModalProps extends ProductDeleteOptions {
  onClose: () => void;
}

export const ProductDeleteModal = (props: ProductDeleteModalProps) => {
  if (!props) {
    return null;
  }
  return <ProductDeleteModalInner {...props} />;
};

const ProductDeleteModalInner = ({
  ids,
  title,
  onDelete,
  onClose,
  collection,
}: ProductDeleteModalProps) => {
  const [productDb, setProductDb] = useAtom(productDbAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const { updateCollectionItems } = useProductOps();

  const deleteProducts = useCallback(
    async (ids: string[]) => {
      const existingIds = ids.filter((i) => productDb.productMap.has(i));
      if (existingIds.length > 0) {
        try {
          const result = await deleteDbProducts("products", existingIds);
          if (result.success) {
            const productMap = new Map(productDb.productMap);
            const categoryMap = new Map(productDb.categoryMap);
            const collectionMap = new Map(productDb.collectionMap);
            result.processed.forEach((id) => {
              const c = categoryMap.get(productMap.get(id)?.product.categoryId);
              if (c) {
                c.totalProductCount--;
              }
              productMap.delete(id);
            });
            removeCollectionProducts(collectionMap, existingIds);
            const db = {
              ...productDb,
              productMap,
              collectionMap,
              version: productDb.version + 1,
            };
            setProductDb(db);

            enqueueSnackbar(
              "Deleted " + result.processed.length + " product(s)",
              {
                variant: "success",
              }
            );
          } else {
            enqueueSnackbar("Error deleting products", {
              variant: "error",
            });
          }
        } catch (error) {
          enqueueSnackbar(
            "Error deleting products: " + (error as Error)?.message,
            {
              variant: "error",
            }
          );
        }
      }
    },
    [enqueueSnackbar, productDb, setProductDb]
  );

  const plural = ids.length > 1 ? "s" : "";
  return (
    <WarningDialog
      open={true}
      onClose={onClose}
      // With a collection in context the primary action is the reversible one:
      // action = remove (onConfirm(false)), secondary = delete (onConfirm(true)).
      onConfirm={async (secondary?: boolean) => {
        const isDelete = !collection || secondary;
        try {
          setDeleteProcessing(true);
          if (isDelete) {
            await deleteProducts(ids);
          } else {
            await updateCollectionItems(collection.id, ids, true);
          }
          onDelete?.();
        } finally {
          setDeleteProcessing(false);
          onClose();
        }
      }}
      title={collection ? `Remove ${title}?` : `Delete ${title}?`}
      // Labels stay short - the collection name lives in the body, not on the
      // button, or a long name wraps the action row onto two lines.
      action={collection ? "Remove" : "Delete"}
      color={collection ? "primary" : "error"}
      secondaryAction={collection ? "Delete everywhere" : undefined}
      secondaryColor={collection ? "error" : undefined}
      processing={deleteProcessing}
    >
      {collection && (
        <p>
          Remove from {collection.name} only takes the product{plural} out of
          that collection. {plural ? "They stay" : "It stays"} in your product
          list and in your other collections.
        </p>
      )}
      {collection && <p>Deleting everywhere cannot be undone:</p>}
      <ul>
        <li>The product{plural} will be deleted from all catalogs!</li>
        <li>
          Product and variant images will be deleted from the storage, other
          products and catalogs as well.
        </li>
      </ul>
    </WarningDialog>
  );
};
